import React, { useRef, useState, useMemo, useCallback } from 'react';
import { computeDailySummary, computeExtendedSummary } from '../engine/metrics';

const TEAL   = '#0cb8a0';
const TEAL2  = '#0a9688';
const WHITE  = '#ffffff';
const DARK   = '#1a2332';
const MUTED  = 'rgba(255,255,255,0.65)';
const CARD_W = 600;
const CARD_H = 390;

function drawRoundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function drawCard(canvas, summary, extended) {
  const ctx = canvas.getContext('2d');
  canvas.width  = CARD_W;
  canvas.height = CARD_H;

  // Background gradient
  const grad = ctx.createLinearGradient(0, 0, CARD_W, CARD_H);
  grad.addColorStop(0, '#0cb8a0');
  grad.addColorStop(1, '#0a7a70');
  ctx.fillStyle = grad;
  drawRoundRect(ctx, 0, 0, CARD_W, CARD_H, 20);
  ctx.fill();

  // Subtle pattern overlay
  ctx.fillStyle = 'rgba(255,255,255,0.04)';
  for (let i = 0; i < 8; i++) {
    ctx.beginPath();
    ctx.arc(CARD_W - 60 + i * 20, 60 + i * 30, 80 - i * 8, 0, Math.PI * 2);
    ctx.fill();
  }

  // ── Logo & date ──────────────────────────────────────────────────────────
  ctx.font = 'bold 22px serif';
  ctx.fillStyle = WHITE;
  ctx.fillText('Mood', 32, 50);
  ctx.font = 'italic bold 22px serif';
  ctx.fillStyle = 'rgba(255,255,255,0.85)';
  ctx.fillText('scope', 32 + ctx.measureText('Mood').width + 1, 50);

  ctx.font = '11px sans-serif';
  ctx.fillStyle = MUTED;
  const date = new Date().toLocaleDateString([], { weekday:'long', month:'long', day:'numeric', year:'numeric' });
  ctx.fillText(date, 32, 70);

  // ── Session verdict ───────────────────────────────────────────────────────
  const q = extended?.sessionQuality ?? null;
  const verdict = q >= 80 ? '🔥 Excellent session'
    : q >= 65 ? '✅ Strong session'
    : q >= 45 ? '⚡ Decent session'
    : q !== null ? '😴 Rest up tomorrow'
    : '📊 Session tracked';

  ctx.font = 'bold 15px sans-serif';
  ctx.fillStyle = 'rgba(255,255,255,0.9)';
  ctx.fillText(verdict, 32, 105);

  // ── Stat boxes ────────────────────────────────────────────────────────────
  const stats = [
    { label: 'Session Quality', val: q !== null ? `${q}` : '—',   unit: '/100' },
    { label: 'Avg Attention',   val: summary?.avgAttentionScore !== null ? `${summary?.avgAttentionScore}` : '—', unit: '/100' },
    { label: 'Longest Focus',   val: `${extended?.longestFlowStreak ?? 0}`, unit: 'min' },
    { label: 'Flow Time',       val: `${extended?.flowMinutes ?? 0}`,        unit: 'min' },
    { label: 'Total Yawns',     val: `${summary?.yawnCount ?? 0}`,           unit: '' },
    { label: 'Fatigue',         val: extended?.fatigueOnsetTime ?? 'None',   unit: '' },
  ];

  const boxW = 158, boxH = 88, gap = 12;
  const startX = 32, startY = 124;

  stats.forEach((s, i) => {
    const col = i % 3;
    const row = Math.floor(i / 3);
    const x = startX + col * (boxW + gap);
    const y = startY + row * (boxH + gap);

    // Box background
    ctx.fillStyle = 'rgba(255,255,255,0.12)';
    drawRoundRect(ctx, x, y, boxW, boxH, 10);
    ctx.fill();

    // Value (large)
    ctx.font = 'bold 28px serif';
    ctx.fillStyle = WHITE;
    ctx.fillText(s.val, x + 14, y + 38);

    // Unit — on same baseline as value, right after it, smaller font
    if (s.unit) {
      const valW = ctx.measureText(s.val).width;
      ctx.font = '11px sans-serif';
      ctx.fillStyle = 'rgba(255,255,255,0.6)';
      ctx.fillText(s.unit, x + 14 + valW + 2, y + 34);
    }

    // Label — fixed bottom position, never overlaps value
    ctx.font = '9px sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.55)';
    ctx.fillText(s.label.toUpperCase(), x + 14, y + boxH - 12);
  });

  // ── Top emotion ──────────────────────────────────────────────────────────
  const topEmotion = summary?.emotionDistribution
    ? Object.entries(summary.emotionDistribution).sort((a,b) => b[1]-a[1])[0]
    : null;

  if (topEmotion) {
    ctx.font = '11px sans-serif';
    ctx.fillStyle = MUTED;
    ctx.fillText(`Top emotion: ${topEmotion[0]} (${topEmotion[1]}%)`, 32, CARD_H - 28);
  }

  // ── URL branding ─────────────────────────────────────────────────────────
  ctx.font = 'bold 11px sans-serif';
  ctx.fillStyle = 'rgba(255,255,255,0.8)';
  const url = '🔗 moodscope.vercel.app';
  ctx.fillText(url, CARD_W - ctx.measureText(url).width - 24, CARD_H - 28);

  return canvas;
}

export function ShareCard({ snapshots, onClose }) {
  const summary  = useMemo(() => computeDailySummary(snapshots),   [snapshots]);
  const extended = useMemo(() => computeExtendedSummary(snapshots), [snapshots]);
  const canvasRef = useRef(null);
  const [copied, setCopied] = useState(false);

  // Draw card when modal opens
  const initCanvas = useCallback((canvas) => {
    if (!canvas) return;
    canvasRef.current = canvas;
    drawCard(canvas, summary, extended);
  }, [summary, extended]);

  const downloadImage = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `moodscope-${new Date().toISOString().slice(0,10)}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const copyText = () => {
    const q = extended?.sessionQuality;
    const a = summary?.avgAttentionScore;
    const f = extended?.flowMinutes ?? 0;
    const streak = extended?.longestFlowStreak ?? 0;
    const text = [
      `My Moodscope session — ${new Date().toLocaleDateString()}`,
      ``,
      `🎯 Session Quality: ${q ?? '—'}/100`,
      `🧠 Avg Attention: ${a ?? '—'}/100`,
      `🌊 Flow Time: ${f} min`,
      `⚡ Longest Focus Streak: ${streak} min`,
      `😴 Yawns: ${summary?.yawnCount ?? 0}`,
      ``,
      `Track your own focus at moodscope.vercel.app`,
    ].join('\n');
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div style={{
      position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center', padding:20,
      backdropFilter:'blur(4px)',
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background:'var(--surface)', border:'1px solid var(--border)', borderRadius:20,
        padding:28, width:'100%', maxWidth:660,
        boxShadow:'0 24px 64px rgba(0,0,0,0.3)',
        display:'flex', flexDirection:'column', gap:16,
      }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <div style={{ fontFamily:"'Fraunces',serif", fontSize:'1.1rem', color:'var(--text)' }}>Share your session</div>
            <div style={{ fontSize:'0.65rem', color:'var(--muted)', marginTop:2 }}>Download as image or copy text summary</div>
          </div>
          <button onClick={onClose} style={{ background:'transparent', border:'none', color:'var(--muted)', fontSize:'1.2rem', cursor:'pointer', lineHeight:1 }}>×</button>
        </div>

        {/* Canvas card preview */}
        <div style={{ borderRadius:14, overflow:'hidden', boxShadow:'0 4px 20px rgba(0,0,0,0.15)', lineHeight:0 }}>
          <canvas ref={initCanvas} style={{ width:'100%', display:'block' }} />
        </div>

        {/* Action buttons */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          <button onClick={downloadImage} style={{
            padding:'12px 0', borderRadius:10, border:'none',
            background:'var(--accent)', color:'#ffffff',
            fontFamily:"'Nunito',sans-serif", fontSize:'0.78rem', fontWeight:600,
            cursor:'pointer', transition:'all 0.2s', display:'flex', alignItems:'center', justifyContent:'center', gap:6,
          }}>
            ⬇ Download Image
          </button>
          <button onClick={copyText} style={{
            padding:'12px 0', borderRadius:10,
            border:'1px solid var(--border)',
            background: copied ? 'var(--surface2)' : 'transparent',
            color: copied ? 'var(--energetic)' : 'var(--muted)',
            fontFamily:"'Nunito',sans-serif", fontSize:'0.78rem', fontWeight:600,
            cursor:'pointer', transition:'all 0.2s',
          }}>
            {copied ? '✓ Copied!' : '📋 Copy Text'}
          </button>
        </div>

        <div style={{ fontSize:'0.6rem', color:'var(--muted)', textAlign:'center', opacity:0.6 }}>
          The image downloads as a PNG. Perfect for LinkedIn, Twitter, or Slack.
        </div>
      </div>
    </div>
  );
}
